import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import * as base from './base'
import { id, SHARED_PROJECT_CACHE, useProjectSharedQuery } from './sharedState'
import { ShippingZoneHelpers, shippingZoneHelpers } from './shippingZoneHelpers'
import { useInvalidateCache } from '../utils'

export function setInitialProject<EXTRA_FIELDS extends string>(
  store: ReturnType<typeof jotai.createStore>,
  slugOrId: string | number,
  project: api.Project<EXTRA_FIELDS>,
): void {
  store.set(SHARED_PROJECT_CACHE, (cache) => {
    cache[id(slugOrId)] = { data: project, status: hooks.QueryStatus.SUCCESS }

    return cache
  })
}

export type UseProject<EXTRA_FIELDS extends string> = {
  cancel: () => Promise<api.HttpResult<unknown> | undefined>
  computeExamples: () => Promise<api.HttpResult<unknown> | undefined>
  end: () => Promise<api.HttpResult<unknown> | undefined>
  like: () => Promise<api.HttpResult<unknown> | undefined>
  mutate: (project: api.Project<EXTRA_FIELDS>) => void
  publish: (payload: api.ProjectPublishRequest) => Promise<api.HttpResult<unknown> | undefined>
  query: () => void
  response: hooks.Response<api.Project<EXTRA_FIELDS>>
  unlike: () => Promise<api.HttpResult<unknown> | undefined>
  update: (patch: api.UpdateProject<EXTRA_FIELDS>) => Promise<hooks.Response<api.Project<EXTRA_FIELDS>>>
  refreshSocials: (provider: api.UserSocialProvider) => Promise<api.HttpResult<unknown> | undefined>
  refreshRewards: () => Promise<api.HttpResult<unknown> | undefined>
  submitForReview: (message: api.ProjectSubmitRequest) => Promise<api.HttpResult<unknown> | undefined>
  createReward: (payload: api.CreateReward) => Promise<api.HttpResult<api.Reward>>
  updateReward: (rewardId: number, payload: api.UpdateReward) => Promise<api.HttpResult<api.Reward>>
  deleteReward: (rewardId: number) => Promise<api.HttpResult<void>>
  fetchVariants: (
    rewardId: number | string,
    valuesIds?: string[],
    withToken?: boolean,
  ) => Promise<api.HttpResult<unknown> | undefined>
} & ShippingZoneHelpers

export function useProject<EXTRA_FIELDS extends string>(
  // FIXME: the same project accessed via slug or id will be stored in two
  // separate entries in cache. If we mutate via slug, the entry via id will not
  // be modified
  slugOrId: string | number,
  config: base.LoadConfig<EXTRA_FIELDS> = {},
): UseProject<EXTRA_FIELDS> {
  const sharedQuery = useProjectSharedQuery(config)

  hooks.useFetchOnMount(
    {
      fetchOnMount: config.fetchOnMount ?? false,
      query,
      getResponse,
    },
    id(slugOrId),
  )

  return {
    response: getResponse(),
    computeExamples,
    like,
    unlike,
    submitForReview,
    query,
    mutate,
    update,
    cancel,
    end,
    publish,
    refreshSocials,
    refreshRewards,
    createReward,
    updateReward,
    deleteReward,
    fetchVariants,
    ...shippingZoneHelpers(slugOrId, sharedQuery),
  }

  function query(): Promise<void> {
    return sharedQuery.query(slugOrId)
  }

  async function update(patch: api.UpdateProject<EXTRA_FIELDS>): Promise<hooks.Response<api.Project<EXTRA_FIELDS>>> {
    if (id(slugOrId) === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    // first, update the local cache
    sharedQuery.mutate([id(slugOrId)], (response) => {
      const currentData = hooks.data(response)

      // if currently there is no data (because cache is empty, or there is no
      // data yet) the first mutation does not modify the cache
      if (currentData === undefined) {
        return response
      }

      // otherwise we update the data in cache with the patch that will be
      // sent to the api
      return {
        data: { ...currentData, ...patch },
        status: hooks.QueryStatus.SUCCESS,
      }
    })

    const projectResponse = getResponse()

    if (projectResponse.data !== undefined) {
      // then update on the api
      const response = await base.update(slugOrId, patch, config)

      // depending on the response, update the cache
      return response.caseOf<hooks.Response<api.Project<EXTRA_FIELDS>>>({
        left: (failure) => {
          sharedQuery.mutate([id(slugOrId)], (response) => {
            return { ...response, failure, status: hooks.QueryStatus.FAILURE }
          })

          return { ...getResponse(), failure, status: hooks.QueryStatus.FAILURE }
        },
        right: (success) => {
          sharedQuery.mutate([id(slugOrId)], { data: success, status: hooks.QueryStatus.SUCCESS })
          return { data: success, status: hooks.QueryStatus.SUCCESS }
        },
      })
    } else {
      return getResponse()
    }
  }

  async function cancel(): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling cancel')
      }

      const result = await base.cancel(slugOrId)
      return result.next((updateProject) => {
        return { ...data, ...updateProject }
      })
    })
  }

  async function computeExamples(): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling computeExamples')
      }

      const result = await base.computeExamples(slugOrId, {
        force_recompute: true,
      })

      return result.next((project) => {
        if (project?.examples) {
          return { ...data, examples: project.examples }
        }

        return data
      })
    })
  }

  async function like(): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data

      if (data === undefined) {
        throw Error('project must be fetched before calling cancel')
      }

      const result = await base.like(slugOrId)

      return result.next((success) => {
        if (success.status === 200) {
          return { ...data, user_role: api.UserRole.Fan }
        }

        return data
      })
    })
  }

  async function unlike(): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data

      if (data === undefined) {
        throw Error('project must be fetched before calling cancel')
      }

      const result = await base.unlike(slugOrId)

      return result.next((success) => {
        if (success.status === 200) {
          return { ...data, user_role: undefined }
        }

        return data
      })
    })
  }

  async function submitForReview(message: api.ProjectSubmitRequest): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling publish')
      }

      const result = await base.submit(slugOrId, message)
      return result.next((submittedProject) => {
        return { ...data, ...submittedProject }
      })
    })
  }

  async function end(): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling cancel')
      }

      const result = await base.end(slugOrId)
      return result.next((updateProject) => {
        return { ...data, ...updateProject }
      })
    })
  }

  async function publish(payload: api.ProjectPublishRequest): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling publish')
      }

      const result = await base.publish(slugOrId, payload)
      return result.next((updateProject) => {
        return { ...data, ...updateProject }
      })
    })
  }

  async function refreshSocials(provider: api.UserSocialProvider): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling publish')
      }

      const result = await base.refreshSocials(slugOrId, provider)

      return result.next((updateProject) => {
        return { ...data, ...updateProject }
      })
    })
  }

  async function refreshRewards(): Promise<api.HttpResult<unknown> | undefined> {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before calling publish')
      }

      const result = await base.rewards(slugOrId)
      return result.next(({ rewards }) => {
        return { ...data, rewards }
      })
    })
  }

  async function createReward(payload: api.CreateReward) {
    const result = await base.createReward(slugOrId, payload)

    return result.nextAsync(async (reward) => {
      // no need to reload the project when running a dryrun since nothing changed
      if (!payload.dry_run) {
        await sharedQuery.query(slugOrId)
      }
      return reward
    })
  }

  async function updateReward(rewardId: number, payload: api.UpdateReward) {
    const result = await base.updateReward(rewardId, payload)

    return result.nextAsync(async (reward) => {
      // no need to reload the project when running a dryrun since nothing changed
      if (!payload.dry_run) {
        await sharedQuery.query(slugOrId)
      }
      return reward
    })
  }

  async function deleteReward(rewardId: number) {
    const result = await base.deleteReward(rewardId)

    return result.nextAsync(async () => {
      await sharedQuery.query(slugOrId)
    })
  }

  async function fetchVariants(rewardId: number | string, valueIds?: string[], withToken?: boolean) {
    if (id(slugOrId) === undefined) {
      return
    }

    return sharedQuery.patch([id(slugOrId)], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('project must be fetched before fetching variants')
      }

      const result = await base.rewardVariants(rewardId, valueIds, withToken)
      return result.next((variantsResponse) => {
        if (variantsResponse.variants.length === 0) {
          return { ...data }
        }
        if ('rewards' in data) {
          const rewards = data.rewards as api.Reward[]
          const rewardIndex = rewards.findIndex((reward) => reward.id === Number(rewardId))
          if (rewardIndex > -1) {
            const mutatedRewards = [...rewards]
            mutatedRewards[rewardIndex].variants = variantsResponse.variants
            const mutatedProject = { ...data, rewards: mutatedRewards }
            sharedQuery.mutate([id(slugOrId)], { data: mutatedProject, status: hooks.QueryStatus.SUCCESS })
            return mutatedProject
          }
        }
        return { ...data }
      })
    })
  }

  function mutate(project: api.Project<EXTRA_FIELDS>): void {
    return sharedQuery.mutate([id(slugOrId)], { data: project, status: hooks.QueryStatus.SUCCESS })
  }

  function getResponse(): hooks.Response<api.Project<EXTRA_FIELDS>> {
    const response = sharedQuery.cache[id(slugOrId)]

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response as hooks.Response<api.Project<EXTRA_FIELDS>>
  }
}

export function useInvalidateProjectCache(projectSlugOrId: string | number | undefined) {
  const setProjectCache = jotai.useSetAtom(SHARED_PROJECT_CACHE)

  return useInvalidateCache(
    (key) => (projectSlugOrId === undefined ? false : key.startsWith(String(projectSlugOrId))),
    setProjectCache,
  )
}
